import { getAddresses } from '@api/address';
import { Container } from '@chakra-ui/react';
import AddAddress from '@components/address/add';
import { AddressContainer } from '@components/container/address';
import { TAddress, TStore } from '@components/types';
import { requestLogin } from '@core/utils';
import { addressState, isLoggedIn } from '@recoil/atoms';
import { Plus } from 'lucide-react';
import router from 'next/router';
import React, { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

interface Props {
  title?: string;
  isDone?: Function;
  heading?: string;
  btnPlacedButtom?: boolean;
  customStyle?: string;
  storeAddress?: TAddress;
  store?:TStore
  px?:string
}

const AddressPanel: React.FC<Props> = ({
  isDone,
  title,
  heading,
  btnPlacedButtom = false,
  storeAddress,
  customStyle,
  px,
  store,
}) => {
  const [savedAddress, setSavedAddress] = useState<TAddress[]>([]);
  const [isShown, setIsShown] = useState(false);
  const [selectAddress, setSelectedAddress] = useRecoilState<TAddress>(addressState);
  const [isEdit, setIsEdit] = useState(true);
  const [success, setSuccess] = useState<boolean>(false);
  const isLogin = useRecoilValue(isLoggedIn);

  const addAddressToList = (address: TAddress) => {
    setSavedAddress((prevAddress = []) => {
      const addressArray = prevAddress.filter((a) => a.id != address?.id);
      addressArray.unshift(address);
      return addressArray;
    });
    setSelectedAddress(address);
  };

  const removeAddressFromList = (address: TAddress) => {
    setSavedAddress((prevAddress = []) =>
      prevAddress.filter((a) => a.id != address?.id)
    );
  };

  const handleAddClick = () => {
    setIsShown(true);
    setIsEdit(false);
  };

  const toggle = (state: boolean) => {
    setIsShown(state);
    setIsEdit(true);
  };

  useEffect(() => {
    const res: Promise<TAddress[]> = getAddresses();
    res
      .then((savedAddress) => setSavedAddress(savedAddress))
      .catch((e) => {
        if (e.response?.status === 401) {
          requestLogin(router.asPath);
        }
      });
  }, []);

  useEffect(() => {
    if (
      selectAddress !== null &&
      selectAddress !== undefined &&
      isShown === false
    ) {
      isDone(true);
    } else {
      isDone(false);
    }
  }, [isShown, selectAddress]);

  return (
    <Container>
      <div className={`flex flex-col justify-center font-manrope ${px ? px : ''}`}>
        {!isShown && (
          <>
            <div className="flex items-center justify-between py-2">
              {heading ? (
                <h2 className="font-bold text-neutral-900 text-lg md:text-xl">
                  {heading}
                </h2>
              ) : null}
              {!success && (
                <button
                  onClick={handleAddClick}
                  className={`flex items-center gap-2 ${
                    // With the bottom bar in use, only phones get it; wider screens keep this one.
                    btnPlacedButtom && 'lt-sm:hidden'
                  } cursor-pointer`}
                >
                  <Plus className="w-4 h-4 text-brand-500" />
                  <span className="text-base text-brand-500 font-manrope font-semibold">
                    ADD NEW ADDRESS
                  </span>
                </button>
              )}
            </div>
            {savedAddress?.length > 0 && (
              <AddressContainer
                customStyle={customStyle}
                savedAddress={savedAddress}
                open={() => {
                  toggle(true);
                }}
                selectAddressCallback={setSelectedAddress}
                removeCallback={(address: TAddress) =>
                  removeAddressFromList(address)
                }
              />
            )}
          </>
        )}

        {!isLogin && success && (
          <div className="flex-1 xl:w-3/5 h-fit  p-4 md:p-6 flex flex-col gap-2 rounded-lg border border-solid border-green-200 bg-green-50">
            <h6 className="text-base text-success-500 font-manrope font-semibold">
              Address submitted! Thanks!
            </h6>
            <p className="text-sm text-success-500 font-manrope font-medium">
              Now, click 'PROCEED' to choose your preferred payment method and
              complete your purchase.
            </p>
          </div>
        )}

        {isShown && (
          <div className="w-full mt-2 justify-center">
            <AddAddress
              setSuccess={setSuccess}
              storeAddress={storeAddress}
              addressCallback={(address: TAddress) => addAddressToList(address)}
              close={() => {
                toggle(false);
              }}
              initialAddress={isEdit ? selectAddress : null}
              store={store}
              isEdit={isEdit}
            />
          </div>
        )}
      </div>
      <div
        className={`hidden ${
          btnPlacedButtom && 'lt-sm:block'
        } items-center align-bottom w-full lt-sm:fixed bottom-0  left-0 right-0 z-40 lt-sm:pt-4 `}
      >
        <button
          onClick={handleAddClick}
          className="md:text-brand-500 font-medium flex items-center gap-2 lt-sm:w-full lt-sm:bg-brand-500 lt-sm:border-brand-500 text-neutral-50 lt-sm:rounded-sm lt-sm:text-sm p-3 lt-sm:justify-center"
        >
          <span>ADD NEW ADDRESS</span>
        </button>
      </div>
    </Container>
  );
};

export default AddressPanel;
